/**
 * AccessVA Tools and Services URL Constants
 *
 * This file contains production and staging URLs for AccessVA tools and services.
 * The appropriate URL set is returned based on the current environment domain
 * and feature toggle states.
 *
 * Environment detection:
 * - staging.va.gov → staging URLs
 * - www.va.gov or domains with 'prod' → production URLs
 * - preview-prod.vfs.va.gov → production URLs
 *
 * Feature Toggles:
 * - Each AccessVA service has a corresponding feature toggle to switch between
 *   legacy URLs and Okta-based URLs (e.g., accessVaBoxOktaurl)
 * - The same toggle names work in both staging and production environments
 * - When enabled, the toggle switches to environment-appropriate Okta URLs
 *
 * Categories:
 * - veterans: Services for Veterans, family members, and service members
 * - business: Services for Business partners, employees, and contractors
 */

// Production Okta URL mappings for AccessVA services
// These URLs are used when the corresponding feature toggle is enabled in PRODUCTION
// Most URLs currently set to legacy URLs until Okta integrations are completed
// Completed Okta integrations: vaCaregiverSupport, grants4vets, mceqVendorPortal, quicksubmit
const PRODUCTION_OKTA_URL_OVERRIDES = {
  box: 'https://login.va.gov/home/va_boxmoderate2534_1/0oa1pj5e93w6dKbq70j7/aln1pj7cmpe9RsekM0j7',
  boxHighImpact: 'https://veteransaffairs-frhext.box.com/',
  ccra: 'https://login.va.gov/app/va_communitycarereferralsandauthorizationccra2198_1/exk1w1tgitFoKSZ380j7/sso/saml',
  centralizedMail:
    'https://keycloak.dmhscarec2.com/realms/dmhs/protocol/openid-connect/auth?client_id=centralized-mail-portal&redirect_uri=https%3A%2F%2Fdmhs.digitalcontentservices.com%2F&response_type=code&scope=openid&kc_idp_hint=ssoe-prod-okta',
  edms: 'https://login.va.gov/home/va_dodliaisoneforceeducationdevelopmentmanagementsystemedms2869_1/0oa1w1o1gdRIg9HuD0j7/aln1w1pz00ftTertp0j7',
  fast: 'https://login.va.gov/home/va_fiduciaryaccountingssubmissiontoolfast2743_1/0oa1rc1wd0UqUfzyf0j7/aln1rc80qqYaEgcdE0j7',
  grants4vets:
    'https://login.va.gov/home/va_nvspsegrants4vets2692_1/0oa1q9nkfpvKiH9Fo0j7/aln1q9q2tzpsABIVn0j7',
  hmis: 'https://www.hmisrepository.va.gov/',
  iamInvitation: 'https://ams.eauth.va.gov/iam-iis-web/',
  kahua: 'https://launch.kahuafn.com/web/',
  lifeInsurance: 'https://eauth.va.gov/einsurance/policyaccess',
  mceqVendorPortal:
    'https://login.va.gov/home/va_mandatorycontractexaminationqualitymceq2686_1/0oa1q9jas3y5ukvzp0j7/aln1q9p1qodL1vN6H0j7',
  moveliqApproval:
    'https://login.va.gov/home/va_movelinqapprover2888_1/0oa1r3b4vkVlUNqkA0j7/aln1r3bje7R7fhTx40j7',
  moveliqEmployee:
    'https://login.va.gov/home/va_movelinqemployee2888_1/0oa1r37a534x4eZNz0j7/aln1r3bn9bX4YSbpP0j7',
  moveliqExpense:
    'https://login.va.gov/home/va_movelinq2888_1/0oa1r36otpCqP0hqm0j7/aln1r3bh2pc1oobPl0j7',
  mvp: 'https://login.va.gov/app/va_millionveteranprogrammvp2228_1/exk1p3fsdwQzI2fN50j7/sso/saml',
  paws: 'https://omhsp-paws-prod.powerappsportals.us/',
  prostheticsVendor:
    'https://login.va.gov/home/va_povidts2777_1/0oa1r36p52RQ34wix0j7/aln1r3a2wk9TBEEM10j7',
  quicksubmit: 'https://login.va.gov/home/bookmark/0oa1wfgm8pqA0JMR60j7/2557',
  roes: 'https://eauth.va.gov/ROESPKI/dalclanding.htm',
  sourcetrack:
    'https://login.va.gov/app/va_sourcematerialtrackingsystemsmts2667_1/exk1pjeyxjQofn0mX0j7/sso/saml',
  sports4vetsOfficials:
    'https://login.va.gov/home/va_nvspsestipends4vets2791_1/0oa1r36ig4PY4CyFZ0j7/aln1r39wq6eFIYL0F0j7',
  sports4vets:
    'https://login.va.gov/home/va_sports4vets2946_1/0oa1rudttjca8fUbi0j7/aln1rugxg6nFWOCEB0j7',
  squares:
    'https://login.va.gov/home/va_squaresssoe2622_1/0oa1reinqcF0gJ5wS0j7/aln1rel8tucZAd7tc0j7',
  vaCaregiverSupport: 'https://www.my.va.gov/CARMAVendor',
  valerir:
    'https://login.va.gov/home/va_valeri2227_1/0oa1smlopq4m5ZmsU0j7/aln1smnr7skjC1niE0j7',
  valife: 'https://eauth.va.gov/valife',
  vetbiz: 'https://portal.vetbiz.va.gov/signin',
  vetHome: 'https://vethome.va.gov/',
  veteranTravelClaim:
    'http://www.va.gov/health-care/file-travel-pay-reimbursement',
  veteransHealthVentureStudio:
    'https://dvagov-vhventures-prod-portal.powerappsportals.us/',
  vetride: 'https://ssp.vetride.va.gov/app/',
  psip: 'https://login.va.gov/home/va_psip2841_1/0oa1sipbjhZ81YHb90j7/aln1sis7ktNFDtRA50j7',
  vhic: 'https://eauth.va.gov/vhic-ss',
  vic: 'https://eauth.va.gov/VIC/accessVA',
  vlogicfm: 'https://www.vlogicfm.us/IWMS/Account/Login',
  vtaIdes:
    'https://login.va.gov/home/va_vta2945_1/0oa1w1nsxdp18GwQM0j7/aln1w1pewjZqewUX90j7',
  yourit:
    'https://login.va.gov/home/va_servicenowyourit2231_1/0oa1vva1xawUmrR0g0j7/aln1vvbnfiFVres5e0j7',
  edelivery: 'https://vaedelivery.gdit.com/',
};

// Staging Okta URL mappings for AccessVA services
// These URLs are used when the corresponding feature toggle is enabled in STAGING
// Most URLs currently set to legacy URLs until Okta integrations are completed
// Completed Okta integrations: vaCaregiverSupport
// Some services have multiple staging environment URLs (array format with labels)
const STAGING_OKTA_URL_OVERRIDES = {
  box: 'https://sqa.eauth.va.gov/accessva/broker?PartnerId=box.net',
  boxHighImpact: 'https://veteransaffairs-frhext-dev.account.box.com/',
  ccra: [
    {
      url: 'https://login-stg.va.gov/home/va-stg_communitycarereferralandauthorizationprojectccra_1/0oa1dh5ghz4bptBhp0j7/aln1dh7trpfYy32rN0j7',
      label: '(SQA)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_communitycarereferralandauthorizationprojectccrapreprd_1/0oa1ekg4nrXspJV6e0j7/aln1ekisyjTcRFvzM0j7',
      label: '(PreProd)',
    },
  ],
  centralizedMail: [
    {
      url: 'https://integrationkeycloak.dmhscarec2integration.com/realms/dmhs/protocol/openid-connect/auth?client_id=centralized-mail-portal&redirect_uri=https%3A%2F%2Fint.dmhscarec2test.com%2F&response_type=code&scope=openid&kc_idp_hint=ssoe-int-okta',
      label: '(Int)',
    },
    {
      url: 'https://uatkeycloak.dmhscarec2test.com/realms/dmhs/protocol/openid-connect/auth?client_id=centralized-mail-portal&redirect_uri=https%3A%2F%2Fuat.dmhscarec2test.com%2F&response_type=code&scope=openid&kc_idp_hint=ssoe-uat-okta',
      label: '(UAT)',
    },
    {
      url: 'https://preprodkeycloak.dmhscarec2test.com/realms/dmhs/protocol/openid-connect/auth?client_id=centralized-mail-portal&redirect_uri=https%3A%2F%2Fdmhs-preprod.digitalcontentservices.com%2F&response_type=code&scope=openid&kc_idp_hint=ssoe-preprod-okta',
      label: '(PreProd)',
    },
  ],
  edms: [
    {
      url: 'https://login-stg.va.gov/home/va-stg_dodliasoneforcessoe2869dev_1/0oa1p5faiyvnVmb3V0j7/aln1p5k8i5LTjRZLt0j7',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_dodliasoneforcessoe2869int_1/0oa1rnispa5ojqEHx0j7/aln1rnn5pr2lt3tPw0j7',
      label: '(INT)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_dodliaisoneforceeducationdevelopmentmanagementsystemedms2869staging_1/0oa1snb8x08vleoIL0j7/aln1sng0ngqBqfd1v0j7',
      label: '(PreProd)',
    },
  ],
  fast: [
    {
      url: 'https://login-stg.va.gov/home/va-stg_fiduciaryaccountingssubmissiontoolfastdev_1/0oa1m1grrkmPIspPt0j7/aln1m1hdvm3o0emKo0j7',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_fast2743int_1/0oa1o1n3yywncWqZq0j7/aln1o1oe1cuz562l30j7',
      label: '(INT)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_fast2743stage_1/0oa1p3a90kfqDHqqx0j7/aln1p3efv8VD21XPu0j7',
      label: '(Staging)',
    },
  ],
  grants4vets: [
    {
      url: 'https://login-stg.va.gov/home/va-stg_nvspseallowancesveteranaccessregistration4vetsdev_1/0oa1m1gz7qJBDF38E0j7/aln1m1is22BCPiDHo0j7',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_nvspsegrants4vets2692int_1/0oa1oeifhvayPmkpe0j7/aln1oell116zSyhqO0j7',
      label: '(Int)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_nvspsegrants4vets2692stage_1/0oa1pjc2vxlrd8Ghi0j7/aln1pjd0ctbDXveVd0j7',
      label: '(Stage)',
    },
  ],
  hmis: [
    { url: 'https://test.hmis.va.gov/', label: '(Int)' },
    { url: 'https://test.hmis.va.gov/', label: '(SQA)' },
    { url: 'https://preprod.hmis.va.gov/', label: '(PreProd)' },
  ],
  iamInvitation: [
    {
      url: 'https://ams.int.iam.va.gov/iam-iis-web',
      label: '(Int)',
    },
    {
      url: 'https://amsvdtapp.sqa.iam.va.gov/iam-iis-web',
      label: '(SQA)',
    },
    {
      url: 'https://amsvdtapp.preprod.iam.va.gov/iam-iis-web',
      label: '(PreProd)',
    },
  ],
  kahua: [
    { url: 'https://prodil2launchdevx.kahua.com/web/', label: '(Int)' },
    { url: 'https://prodil2launchdevx.kahua.com/web/', label: '(SQA)' },
    { url: 'https://launch.kahuafn.com/web/', label: '(PreProd)' },
  ],
  lifeInsurance: 'https://insuranceuat.vba.va.gov/home',
  mceqVendorPortal: [
    {
      url: 'https://login-stg.va.gov/home/va-stg_mandatorycontractexaminationqualitymceqdev_1/0oa1m1ewr7uIqGnUG0j7/aln1m1hmdhpOyZBgX0j7',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_mceq2686int_1/0oa1nz7aphMTVX4pf0j7/aln1nz8xaryDe47f10j7',
      label: '(INT)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_mceq2686stage_1/0oa1p3co6bmrMlCPi0j7/aln1p3f0snGFTLFDE0j7',
      label: '(Staging)',
    },
  ],
  moveliqApproval: 'https://imove-approver.mlinqsonlinetest.net/',
  moveliqEmployee: 'https://imove-employee.mlinqsonlinetest.net/',
  moveliqExpense: 'https://imove.mlinqsonlinetest.net/ReloLogin.aspx',
  mvp: [
    {
      url: 'https://login-stg.va.gov/app/va-stg_millionveteranprogrammvp2228dev2_1/exk1qxqcimRt2fyR40j7/sso/saml',
      label: '(Dev2)',
    },
    {
      url: 'https://login-stg.va.gov/app/va-stg_millionveteranprogrammvp2228dev_1/exk1qxsljjfMHpjQQ0j7/sso/saml',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/app/va-stg_mvpsqa_1/exk1ebbpqk4BxiNFl0j7/sso/saml',
      label: '(SQA)',
    },
    {
      url: 'https://login-stg.va.gov/app/va-stg_millionveteranprogrammvppreprod_1/exk1ffdixjDX5nzPK0j7/sso/saml',
      label: '(PreProd)',
    },
    {
      url: 'https://login-stg.va.gov/app/va-stg_millionveteranprogrammvpmaverick_1/exk1ffbvrkbmGPGHT0j7/sso/saml',
      label: '(Maverick)',
    },
  ],
  paws: 'https://omhsp-paws-dev.powerappsportals.us/',
  prostheticsVendor: [
    {
      url: 'https://login-stg.va.gov/home/va-stg_prostheticordervendorinterfaceanddeliverytrackingsolutionpovidtsdev_1/0oa1lsaj6ySuQGdPC0j7/aln1lsewkoHKfVXk60j7',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_povidts2777int_1/0oa1oekg3rWlLcxn40j7/aln1oenme9ykMmbKc0j7',
      label: '(INT)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_povidts2777stage_1/0oa1pjcat2ilGBw980j7/aln1pjdre24ZFdPIg0j7',
      label: '(Stage)',
    },
  ],
  quicksubmit: [
    {
      url: 'https://login-stg.va.gov/app/va-stg_quicksubmitdev_1/exk1kdemus0YJRWKt0j7/sso/saml',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/app/va-stg_quicksubmitsqa_1/exk1m3k6n1j0nPxKy0j7/sso/saml',
      label: '(Test)',
    },
    {
      url: 'https://login-stg.va.gov/app/va-stg_quicksubmitpreprod_1/exk1m3msiacBzKmky0j7/sso/saml',
      label: '(Staging)',
    },
  ],
  roes: 'https://sqa.eauth.va.gov/ROESPKI/dalclanding.htm',
  sourcetrack: [
    {
      url: 'https://login-stg.va.gov/app/va-stg_sourcematerialtrackingsystemsmtsdev_1/exk1ekmq0zIJ3npeq0j7/sso/saml',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/app/va-stg_sourcematerialtrackingsystemsmtssqa_1/exk1m30dur5d7GgsY0j7/sso/saml',
      label: '(Staging)',
    },
    {
      url: 'https://login-stg.va.gov/app/va-stg_sourcematerialtrackingsystemsmtsprepod_1/exk1m30rnyBgCJI8G0j7/sso/saml',
      label: '(PreProd)',
    },
  ],
  sports4vetsOfficials: [
    {
      url: 'https://login-stg.va.gov/home/va-stg_nvspsestipends4vets2791dev_1/0oa1mtjhhtZyWeGaG0j7/aln1mtm4oyf5a4l4r0j7',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_nvspsestipends4vets2791int_1/0oa1oelf1b1rC3gl40j7/aln1oen2b4EFgiQ7e0j7',
      label: '(INT)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_nvspsestipends4vets2791stage_1/0oa1pjdiymvj4VUrj0j7/aln1pjebh7EiN6cK20j7',
      label: '(Stage)',
    },
  ],
  sports4vets: [
    {
      url: 'https://login-stg.va.gov/home/va-stg_sports4vets2946int_1/0oa1oo5bz90Z9JEVr0j7/aln1oo9v92Q79pZJV0j7',
      label: '(Int)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_sports4vets2946_1/0oa1o36dovvuhwUfR0j7/aln1o393yaCnKAkWk0j7',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_sports4vets2946staging_1/0oa1qodi0wC3uOeCh0j7/aln1qoj5ohFSLLKKq0j7',
      label: '(PreProd)',
    },
  ],
  squares: [
    {
      url: 'https://login-stg.va.gov/home/va-stg_squaresqa_1/0oa1jwvqcjxO3SW2J0j7/aln1jx1drz747YcEz0j7',
      label: '(QA)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_squaresssoedev_1/0oa1lrupkbH1ozeP80j7/aln1lrypwwJXLfiUM0j7',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_squaresssoe2622int_1/0oa1my6mh8kphwFUh0j7/aln1my7eqaqhIJh300j7',
      label: '(INT)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_squaresssoe2622perf_1/0oa1nora612vxL7IX0j7/aln1nou2yzrB1XSrB0j7',
      label: '(PERF)',
    },
  ],
  vaCaregiverSupport: [
    {
      url: 'https://va--ssoedev.sandbox.my.site.com/CARMAVendor',
      label: '(Dev)',
    },
    { url: 'https://www.my.int.va.gov/CARMAVendor', label: '(INT)' },
    { url: 'https://www.my.staging.va.gov/CARMAVendor', label: '(Staging)' },
  ],
  valerir: [
    {
      url: 'https://va--ssoedev.sandbox.my.site.com/VALERI',
      label: '(Dev)',
    },
    {
      url: 'https://www.my.int.va.gov/VALERI',
      label: '(Int)',
    },
    {
      url: 'https://www.my.staging.va.gov/VALERI',
      label: '(Staging)',
    },
  ],
  valife: 'https://insuranceuat.vba.va.gov/home',
  vetbiz: [
    {
      url: 'https://vip-vems-dev.powerappsportals.us/SignIn',
      label: '(Int)',
    },
    {
      url: 'https://vip-vems-qa.powerappsportals.us/SignIn',
      label: '(SQA)',
    },
    {
      url: 'https://vip-vems-preprod.powerappsportals.us/SignIn',
      label: '(PreProd)',
    },
  ],
  vetHome: [
    { url: 'https://vethome-dev.powerappsportals.us/', label: '(Int)' },
    { url: 'https://vethome-qa.powerappsportals.us/', label: '(SQA)' },
    { url: 'https://vethome-preprod.powerappsportals.us/', label: '(PreProd)' },
  ],
  veteranTravelClaim: [
    {
      url: 'https://dev2-benetravelclaims-va.powerappsportals.us/',
      label: '(Int)',
    },
    { url: 'https://dvagov-btsss-qa.dynamics365portals.us', label: '(SQA)' },
    {
      url: 'https://dvagov-btsss-qa.dynamics365portals.us',
      label: '(PreProd)',
    },
  ],
  veteransHealthVentureStudio: [
    {
      url: 'https://dvagov-vhventures-dev-portal.powerappsportals.us',
      label: '(Int)',
    },
    {
      url: 'https://dvagov-vhventures-test-portal.powerappsportals.us',
      label: '(SQA)',
    },
    {
      url: 'https://dvagov-vhventures-test-portal.powerappsportals.us',
      label: '(PreProd)',
    },
  ],
  vetride:
    'https://staging.va.gov/sign-in/?app_name=VetRide+%5B2069%5D+Staging&client_id=okta_stg&code_challenge=bxEz1CS0OkpTl6pRB8h5XiQp9JUclaJeV6cK0O5QcfI&code_challenge_method=S256&oauth=true&state=Y1Z5TkJKQzg4ZFo5Ky9TNVFSbEhDTURkNUtneCt4V1hzKzNWNVc0NzhCeUxtaEJMNmo5cXdxLzlOSVJpdlNvWg',
  vhic: 'https://sqa.eauth.va.gov/vhic-ss',
  vic: [
    {
      url: 'https://int.eauth.va.gov/vic',
      label: '(Int)',
    },
    {
      url: 'https://sqa.eauth.va.gov/vic',
      label: '(SQA)',
    },
    {
      url: 'https://preprod.eauth.va.gov/vic',
      label: '(PreProd)',
    },
  ],
  vlogicfm: [
    { url: 'https://test.vlogicfm.us', label: '(Int)' },
    { url: 'https://test.vlogicfm.us', label: '(SQA)' },
    { url: 'https://training.vlogicfm.us', label: '(PreProd)' },
  ],
  vtaIdes: [
    {
      url: 'https://login-stg.va.gov/home/va-stg_vtassoe2945int_1/0oa1xpkieaWRooxcL0j7/aln1xpoqmkmq6URqo0j7',
      label: '(Int)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_vtassoe2945dev_1/0oa1p5fd38T0wTiTo0j7/aln1p5ka24kBaSHFA0j7',
      label: '(DEV)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_vtassoe2945preprod_1/0oa1yqur10Qvz7YXo0j7/aln1yqvfq9fkbJ9LV0j7',
      label: '(PreProd)',
    },
  ],
  yourit: [
    {
      url: 'https://login-stg.va.gov/home/va-stg_test_1/0oa1m0xi5lo1jr0ZR0j7/aln1m0yppa2JUjx7b0j7',
      label: '(Dev)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_servicenowyourit2231nonproddevii_1/0oa1m21mfouQ8D7TV0j7/aln1m250iduR82Lb30j7',
      label: '(Dev II)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_servicenowyourit2231sqa_2/0oa1pmsiejOzLeitj0j7/aln1pmtjs9GJxdbv00j7',
      label: '(SQA)',
    },
    {
      url: 'https://login-stg.va.gov/home/va-stg_servicenowyourit2231nonprodsubprod1_1/0oa1m202vwgSUP7iW0j7/aln1m250ijBuN8Y8g0j7',
      label: '(SubProd1)',
    },
  ],
  edelivery: [
    { url: 'https://test.vaedelivery.gdit.com', label: '(SQA)' },
    { url: 'https://preprod.vaedelivery.ucap.gdit.com', label: '(PreProd)' },
  ],
};

// Production URLs
const PRODUCTION_LINKS = {
  veterans: [
    { text: 'Ask VA', url: 'https://www.va.gov/contact-us/ask-va/' },
    {
      text: 'Fiduciary Accountings Submission Tool (FAST)',
      url: 'https://www.my.va.gov/FAST',
    },
    {
      text: 'Life insurance online policy access',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=einsurance',
    },
    {
      text: 'Million Veteran Program (MVP)',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=mvp',
    },
    {
      text: 'QuickSubmit Upload Tool',
      url: 'https://login.va.gov/home/bookmark/0oa1wfgm8pqA0JMR60j7/2557',
    },
    {
      text: 'Sports4Vets',
      url: 'https://www.my.va.gov/Veterans',
    },
    {
      text: 'VA Life Insurance (VALife)',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=valife',
    },
    { text: 'VA eDelivery', url: 'https://vaedelivery.gdit.com/' },
    {
      text: 'VetBiz Portal',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=vetbiz',
    },
    { text: 'VetRide', url: 'https://ssp.vetride.va.gov/app/' },
    {
      text: 'Veterans Exposure Team - Health Outcomes Military Exposures (VET-HOME)',
      url: 'https://vethome.va.gov/',
    },
    {
      text: 'Veterans Health Venture Studio',
      url: 'https://dvagov-vhventures-prod-portal.powerappsportals.us/',
    },
    {
      text: 'Veterans ID Card (VIC)',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=vic',
    },
    {
      text: 'Veteran travel claim entry',
      url: 'http://www.va.gov/health-care/file-travel-pay-reimbursement',
    },
    {
      text: 'Veteran Health Identification Card (VHIC)',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=vhic-ss',
    },
  ],
  business: [
    { text: 'Ask VA', url: 'https://www.va.gov/contact-us/ask-va/' },
    { text: 'Box', url: 'https://eauth.va.gov/accessva/?cspSelectFor=box' },
    { text: 'Box High Impact', url: 'https://veteransaffairs-frhext.box.com/' },
    {
      text: 'VA Caregiver Support',
      url: 'https://www.my.va.gov/CARMAVendor',
    },
    {
      text: 'Community Care Referrals and Authorization (CCR&A)',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=ccra',
    },
    {
      text: 'Centralized Mail Portal',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=dmhs',
    },
    {
      text: 'Education Development Management System (EDMS)',
      url: 'https://www.my.va.gov/DODLiaison',
    },
    {
      text: 'Fiduciary Accountings Submission Tool (FAST)',
      url: 'https://www.my.va.gov/FAST',
    },
    {
      text: 'PCS Travel / moveLINQ approval portal',
      url: 'https://imove-approver.mlinqsonline.net/',
    },
    {
      text: 'PCS Travel / moveLINQ employee portal',
      url: 'https://imove-employee.mlinqsonline.net/',
    },
    {
      text: 'PCS Travel / moveLINQ expense portal',
      url: 'https://imove.mlinqsonline.net/',
    },
    { text: 'Grants4Vets', url: 'https://www.my.va.gov/nvspsegrantee' },
    {
      text: 'Homeless Management Information System (HMIS)',
      url: 'https://www.hmisrepository.va.gov/',
    },
    {
      text: 'IAM Invitation Service',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=iamiis',
    },
    { text: 'Kahua', url: 'https://launch.kahuafn.com/web/' },
    {
      text: 'MCEQ Vendor Portal',
      url: 'https://www.my.va.gov/MCEQVendorPortal',
    },
    {
      text: 'Personnel Security Investigations Portal (PSIP) (HCS)',
      url: 'https://www.my.vavet.va.gov/applicantportal',
    },
    { text: 'Prosthetics Vendor Portal', url: 'https://www.my.va.gov/PVP' },
    {
      text: 'Puppies Assisting Wounded Service Members',
      url: 'https://omhsp-paws-prod.powerappsportals.us/',
    },
    {
      text: 'QuickSubmit Upload Tool (includes PIV)',
      url: 'https://login.va.gov/app/va_quicksubmit2667_1/exk1q9erdmmzB5S9f0j7/sso/saml',
    },
    {
      text: 'Remote Order Entry System (ROES)',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=roes',
    },
    {
      text: 'SourceTrack',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=smts',
    },
    {
      text: 'Sports4Vets for certifying officials and NVGAG volunteers',
      url: 'https://www.my.va.gov/stipends4vets',
    },
    { text: 'Squares', url: 'https://www.my.vaemcc.va.gov/SQUARES' },
    {
      text: 'VA Loan Electronic Reporting Interface Reengineering (VALERI-R)',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=valerir',
    },
    {
      text: 'VetBiz Portal (VetBiz)',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=vetbiz',
    },
    {
      text: 'Veterans ID Card (VIC)',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=vic',
    },
    {
      text: 'Veterans Health Venture Studio',
      url: 'https://dvagov-vhventures-prod-portal.powerappsportals.us/',
    },
    { text: 'VTA IDES', url: 'https://www.my.vavet.va.gov/AccessVTA' },
    { text: 'VLogicFM', url: 'https://www.vlogicfm.us/' },
    { text: 'VA eDelivery', url: 'https://vaedelivery.gdit.com/' },
    {
      text: 'yourIT - Employee Self Service',
      url: 'https://eauth.va.gov/accessva/?cspSelectFor=yourit&targetPath=/selfservice',
    },
  ],
};

// Staging URLs
const STAGING_LINKS = {
  veterans: [
    { text: 'Ask VA', url: 'https://www.va.gov/contact-us/ask-va/' },
    {
      text: 'Fiduciary Accountings Submission Tool (FAST) (Dev)',
      url: 'https://login-stg.va.gov/home/va-stg_fiduciaryaccountingssubmissiontoolfastdev_1/0oa1m1grrkmPIspPt0j7/aln1m1hdvm3o0emKo0j7',
    },
    {
      text: 'Fiduciary Accountings Submission Tool (FAST) (INT)',
      url: 'https://login-stg.va.gov/home/va-stg_fast2743int_1/0oa1o1n3yywncWqZq0j7/aln1o1oe1cuz562l30j7',
    },
    {
      text: 'Fiduciary Accountings Submission Tool (FAST) (Staging)',
      url: 'https://login-stg.va.gov/home/va-stg_fast2743stage_1/0oa1p3a90kfqDHqqx0j7/aln1p3efv8VD21XPu0j7',
    },
    {
      text: 'Life insurance online policy access (UAT)',
      url: 'https://insuranceuat.vba.va.gov/home',
    },
    {
      text: 'Million Veteran Program (MVP) (Dev)',
      url: 'https://login-stg.va.gov/app/va-stg_millionveteranprogrammvp2228dev_1/exk1qxsljjfMHpjQQ0j7/sso/saml',
    },
    {
      text: 'Million Veteran Program (MVP) (Dev2)',
      url: 'https://login-stg.va.gov/app/va-stg_millionveteranprogrammvp2228dev2_1/exk1qxqcimRt2fyR40j7/sso/saml',
    },
    {
      text: 'Million Veteran Program (MVP) (SQA)',
      url: 'https://login-stg.va.gov/app/va-stg_mvpsqa_1/exk1ebbpqk4BxiNFl0j7/sso/saml',
    },
    {
      text: 'Million Veteran Program (MVP) (PreProd)',
      url: 'https://login-stg.va.gov/app/va-stg_millionveteranprogrammvppreprod_1/exk1ffdixjDX5nzPK0j7/sso/saml',
    },
    {
      text: 'Million Veteran Program (MVP) (Maverick)',
      url: 'https://login-stg.va.gov/app/va-stg_millionveteranprogrammvpmaverick_1/exk1ffbvrkbmGPGHT0j7/sso/saml',
    },
    {
      text: 'QuickSubmit Upload Tool (Dev)',
      url: 'https://login-stg.va.gov/home/bookmark/0oa1thizbaKh7tqmj0j7/2557',
    },
    {
      text: 'QuickSubmit Upload Tool (Test)',
      url: 'https://login-stg.va.gov/home/bookmark/0oa1thiysieVccB7g0j7/2557',
    },
    {
      text: 'QuickSubmit Upload Tool (Staging)',
      url: 'https://login-stg.va.gov/home/bookmark/0oa1thj3efgqyLXHi0j7/2557',
    },
    {
      text: 'Sports4Vets (Int)',
      url: 'https://login-stg.va.gov/home/va-stg_sports4vets2946int_1/0oa1oo5bz90Z9JEVr0j7/aln1oo9v92Q79pZJV0j7',
    },
    {
      text: 'Sports4Vets (Dev)',
      url: 'https://login-stg.va.gov/home/va-stg_sports4vets2946_1/0oa1o36dovvuhwUfR0j7/aln1o393yaCnKAkWk0j7',
    },
    {
      text: 'Sports4Vets (PreProd)',
      url: 'https://login-stg.va.gov/home/va-stg_sports4vets2946staging_1/0oa1qodi0wC3uOeCh0j7/aln1qoj5ohFSLLKKq0j7',
    },
    {
      text: 'VA Life Insurance (VALife)',
      url: 'https://sqa.eauth.va.gov/einsurance/VALIFE',
    },
    {
      text: 'VetRide',
      url: 'https://sqa.eauth.va.gov/accessva/broker?PartnerId=https://staging-ssp.vetride.va.gov/saml2/service-provider-metadata/ssoe',
    },
    {
      text: 'Veterans Exposure Team - Health Outcomes Military Exposures (VET-HOME) (Int)',
      url: 'https://vethome-dev.powerappsportals.us/',
    },
    {
      text: 'Veterans Exposure Team - Health Outcomes Military Exposures (VET-HOME) (SQA)',
      url: 'https://vethome-qa.powerappsportals.us/',
    },
    {
      text: 'Veterans Exposure Team - Health Outcomes Military Exposures (VET-HOME) (PreProd)',
      url: 'https://vethome-preprod.powerappsportals.us/',
    },
    {
      text: 'Veterans Health Venture Studio (Int)',
      url: 'https://dvagov-vhventures-dev-portal.powerappsportals.us',
    },
    {
      text: 'Veterans Health Venture Studio (SQA)',
      url: 'https://dvagov-vhventures-test-portal.powerappsportals.us',
    },
    {
      text: 'Veterans Health Venture Studio (PreProd)',
      url: 'https://dvagov-vhventures-test-portal.powerappsportals.us',
    },
    {
      text: 'Veterans ID Card (VIC) (Int)',
      url: 'https://int.eauth.va.gov/accessva/?cspSelectFor=vic',
    },
    {
      text: 'Veterans ID Card (VIC) (SQA)',
      url: 'https://sqa.eauth.va.gov/accessva/?cspSelectFor=vic',
    },
    {
      text: 'Veterans ID Card (VIC) (PreProd)',
      url: 'https://preprod.eauth.va.gov/accessva/?cspSelectFor=vic',
    },
    {
      text: 'Veteran travel claim entry (Int)',
      url: 'https://dev2-benetravelclaims-va.powerappsportals.us/',
    },
    {
      text: 'Veteran travel claim entry (SQA)',
      url: 'https://dvagov-btsss-qa.dynamics365portals.us',
    },
    {
      text: 'Veteran travel claim entry (PreProd)',
      url: 'https://dvagov-btsss-qa.dynamics365portals.us',
    },
    {
      text: 'Veteran Health Identification Card (VHIC)',
      url: 'https://sqa.eauth.va.gov/accessva/?cspSelectFor=vhic-ss',
    },
  ],
  business: [
    { text: 'Ask VA', url: 'https://www.va.gov/contact-us/ask-va/' },
    {
      text: 'Box',
      url: 'https://sqa.eauth.va.gov/accessva/broker?PartnerId=box.net',
    },
    {
      text: 'Box High Impact',
      url: 'https://veteransaffairs-frhext-dev.account.box.com/',
    },
    {
      text: 'VA Caregiver Support',
      url: 'https://www.my.staging.va.gov/CARMAVendor',
    },
    {
      text: 'Community Care Referrals and Authorization (CCR&A) (SQA)',
      url: 'https://gov-tic-ipe-eweb.ccra.va.gov/',
    },
    {
      text: 'Community Care Referrals and Authorization (CCR&A) (PreProd)',
      url: 'https://preprod.vaedelivery.ucap.gdit.com',
    },
    {
      text: 'Centralized Mail Portal (Int)',
      url: 'https://integrationkeycloak.dmhscarec2integration.com/realms/dmhs/protocol/openid-connect/auth?client_id=centralized-mail-portal&redirect_uri=https%3A%2F%2Fint.dmhscarec2test.com%2F&response_type=code&scope=openid&kc_idp_hint=ssoe-int-okta',
    },
    {
      text: 'Centralized Mail Portal (UAT)',
      url: 'https://uatkeycloak.dmhscarec2test.com/realms/dmhs/protocol/openid-connect/auth?client_id=centralized-mail-portal&redirect_uri=https%3A%2F%2Fuat.dmhscarec2test.com%2F&response_type=code&scope=openid&kc_idp_hint=ssoe-uat-okta',
    },
    {
      text: 'Centralized Mail Portal (PreProd)',
      url: 'https://preprodkeycloak.dmhscarec2test.com/realms/dmhs/protocol/openid-connect/auth?client_id=centralized-mail-portal&redirect_uri=https%3A%2F%2Fdmhs-preprod.digitalcontentservices.com%2F&response_type=code&scope=openid&kc_idp_hint=ssoe-preprod-okta',
    },
    {
      text: 'Education Development Management System (EDMS) (Dev)',
      url: 'https://login-stg.va.gov/home/va-stg_dodliasoneforcessoe2869dev_1/0oa1p5faiyvnVmb3V0j7/aln1p5k8i5LTjRZLt0j7',
    },
    {
      text: 'Education Development Management System (EDMS) (INT)',
      url: 'https://login-stg.va.gov/home/va-stg_dodliasoneforcessoe2869int_1/0oa1rnispa5ojqEHx0j7/aln1rnn5pr2lt3tPw0j7',
    },
    {
      text: 'Education Development Management System (EDMS) (PreProd)',
      url: 'https://login-stg.va.gov/home/va-stg_dodliaisoneforceeducationdevelopmentmanagementsystemedms2869staging_1/0oa1snb8x08vleoIL0j7/aln1sng0ngqBqfd1v0j7',
    },
    {
      text: 'Fiduciary Accountings Submission Tool (FAST) (Dev)',
      url: 'https://login-stg.va.gov/home/va-stg_fiduciaryaccountingssubmissiontoolfastdev_1/0oa1m1grrkmPIspPt0j7/aln1m1hdvm3o0emKo0j7',
    },
    {
      text: 'Fiduciary Accountings Submission Tool (FAST) (INT)',
      url: 'https://login-stg.va.gov/home/va-stg_fast2743int_1/0oa1o1n3yywncWqZq0j7/aln1o1oe1cuz562l30j7',
    },
    {
      text: 'Fiduciary Accountings Submission Tool (FAST) (Staging)',
      url: 'https://login-stg.va.gov/home/va-stg_fast2743stage_1/0oa1p3a90kfqDHqqx0j7/aln1p3efv8VD21XPu0j7',
    },
    {
      text: 'Grants4Vets (Dev)',
      url: 'https://login-stg.va.gov/home/va-stg_nvspseallowancesveteranaccessregistration4vetsdev_1/0oa1m1gz7qJBDF38E0j7/aln1m1is22BCPiDHo0j7',
    },
    {
      text: 'Grants4Vets (Int)',
      url: 'https://login-stg.va.gov/home/va-stg_nvspsegrants4vets2692int_1/0oa1oeifhvayPmkpe0j7/aln1oell116zSyhqO0j7',
    },
    {
      text: 'Grants4Vets (Stage)',
      url: 'https://login-stg.va.gov/home/va-stg_nvspsegrants4vets2692stage_1/0oa1pjc2vxlrd8Ghi0j7/aln1pjd0ctbDXveVd0j7',
    },
    {
      text: 'Homeless Management Information System (HMIS) (Int)',
      url: 'https://test.hmis.va.gov/',
    },
    {
      text: 'Homeless Management Information System (HMIS) (SQA)',
      url: 'https://test.hmis.va.gov/',
    },
    {
      text: 'Homeless Management Information System (HMIS) (PreProd)',
      url: 'https://preprod.hmis.va.gov/',
    },
    {
      text: 'IAM Invitation Service (Int)',
      url: 'https://int.eauth.va.gov/accessva/?cspSelectFor=iamiis',
    },
    {
      text: 'IAM Invitation Service (SQA)',
      url: 'https://sqa.eauth.va.gov/accessva/?cspSelectFor=iamiis',
    },
    {
      text: 'IAM Invitation Service (PreProd)',
      url: 'https://preprod.eauth.va.gov/accessva/?cspSelectFor=iamiis',
    },
    {
      text: 'PCS Travel / moveLINQ approval portal (SQA)',
      url: 'https://imove-approver.mlinqsonlinetest.net/',
    },
    {
      text: 'PCS Travel / moveLINQ employee portal (Dev)',
      url: 'https://imove-employee.mlinqsonlinetest.net/',
    },
    {
      text: 'PCS Travel / moveLINQ expense portal (Dev)',
      url: 'https://imove.mlinqsonlinetest.net/ReloLogin.aspx',
    },
    {
      text: 'Kahua (Int)',
      url: 'https://prodil2launchdevx.kahua.com/web/',
    },
    {
      text: 'Kahua (SQA)',
      url: 'https://prodil2launchdevx.kahua.com/web/',
    },
    {
      text: 'Kahua (PreProd)',
      url: 'https://launch.kahuafn.com/web/',
    },
    {
      text: 'Life insurance online policy access (UAT)',
      url: 'https://insuranceuat.vba.va.gov/home',
    },
    {
      text: 'MCEQ Vendor Portal (Dev)',
      url: 'https://login-stg.va.gov/home/va-stg_mandatorycontractexaminationqualitymceqdev_1/0oa1m1ewr7uIqGnUG0j7/aln1m1hmdhpOyZBgX0j7',
    },
    {
      text: 'MCEQ Vendor Portal (INT)',
      url: 'https://login-stg.va.gov/home/va-stg_mceq2686int_1/0oa1nz7aphMTVX4pf0j7/aln1nz8xaryDe47f10j7',
    },
    {
      text: 'MCEQ Vendor Portal (Staging)',
      url: 'https://login-stg.va.gov/home/va-stg_mceq2686stage_1/0oa1p3co6bmrMlCPi0j7/aln1p3f0snGFTLFDE0j7',
    },
    {
      text: 'Personnel Security Investigations Portal (PSIP) (HCS) (INT)',
      url: 'https://login-stg.va.gov/home/va-stg_psip2841int_1/0oa1p67ysfTnY3WSt0j7/aln1p6eani2G5CgQ10j7',
    },
    {
      text: 'Personnel Security Investigations Portal (PSIP) (HCS) (Perf)',
      url: 'https://login-stg.va.gov/home/va-stg_psip2841perf_1/0oa1ret2m3ii6IV2i0j7/aln1revdv1x7HxYVa0j7',
    },
    {
      text: 'Puppies Assisting Wounded Service Members (Dev)',
      url: 'https://omhsp-paws-dev.powerappsportals.us/',
    },
    {
      text: 'Puppies Assisting Wounded Service Members (Test)',
      url: 'https://omhsp-paws-test.powerappsportals.us/',
    },
    {
      text: 'Prosthetics Vendor Portal (Dev)',
      url: 'https://login-stg.va.gov/home/va-stg_prostheticordervendorinterfaceanddeliverytrackingsolutionpovidtsdev_1/0oa1lsaj6ySuQGdPC0j7/aln1lsewkoHKfVXk60j7',
    },
    {
      text: 'Prosthetics Vendor Portal (INT)',
      url: 'https://login-stg.va.gov/home/va-stg_povidts2777int_1/0oa1oekg3rWlLcxn40j7/aln1oenme9ykMmbKc0j7',
    },
    {
      text: 'Prosthetics Vendor Portal (Stage)',
      url: 'https://login-stg.va.gov/home/va-stg_povidts2777stage_1/0oa1pjcat2ilGBw980j7/aln1pjdre24ZFdPIg0j7',
    },
    {
      text: 'QuickSubmit Upload Tool (includes PIV) (Dev)',
      url: 'https://login-stg.va.gov/login/signout?fromURI=https://login-stg.va.gov/app/va-stg_quicksubmitdev_1/exk1kdemus0YJRWKt0j7/sso/saml',
    },
    {
      text: 'QuickSubmit Upload Tool (includes PIV) (Test)',
      url: 'https://login-stg.va.gov/login/signout?fromURI=https://login-stg.va.gov/app/va-stg_quicksubmitsqa_1/exk1m3k6n1j0nPxKy0j7/sso/saml',
    },
    {
      text: 'QuickSubmit Upload Tool (includes PIV) (Staging)',
      url: 'https://login-stg.va.gov/login/signout?fromURI=https://login-stg.va.gov/app/va-stg_quicksubmitpreprod_1/exk1m3msiacBzKmky0j7/sso/saml',
    },
    {
      text: 'Remote Order Entry System (ROES) (SQA)',
      url: 'https://sqa.eauth.va.gov/accessva/?cspSelectFor=roes',
    },
    {
      text: 'SourceTrack (Dev)',
      url: 'https://dev.smts.gcio.com',
    },
    {
      text: 'SourceTrack (Staging)',
      url: 'https://test.smts.gcio.com',
    },
    {
      text: 'SourceTrack (PreProd)',
      url: 'https://staging.smts.gcio.com',
    },
    {
      text: 'Sports4Vets (Int)',
      url: 'https://login-stg.va.gov/home/va-stg_sports4vets2946int_1/0oa1oo5bz90Z9JEVr0j7/aln1oo9v92Q79pZJV0j7',
    },
    {
      text: 'Sports4Vets (Dev)',
      url: 'https://login-stg.va.gov/home/va-stg_sports4vets2946_1/0oa1o36dovvuhwUfR0j7/aln1o393yaCnKAkWk0j7',
    },
    {
      text: 'Sports4Vets (PreProd)',
      url: 'https://login-stg.va.gov/home/va-stg_sports4vets2946staging_1/0oa1qodi0wC3uOeCh0j7/aln1qoj5ohFSLLKKq0j7',
    },
    {
      text: 'Sports4Vets for certifying officials and NVGAG volunteers (Dev)',
      url: 'https://login-stg.va.gov/home/va-stg_nvspsestipends4vets2791dev_1/0oa1mtjhhtZyWeGaG0j7/aln1mtm4oyf5a4l4r0j7',
    },
    {
      text: 'Sports4Vets for certifying officials and NVGAG volunteers (INT)',
      url: 'https://login-stg.va.gov/home/va-stg_nvspsestipends4vets2791int_1/0oa1oelf1b1rC3gl40j7/aln1oen2b4EFgiQ7e0j7',
    },
    {
      text: 'Sports4Vets for certifying officials and NVGAG volunteers (Stage)',
      url: 'https://login-stg.va.gov/home/va-stg_nvspsestipends4vets2791stage_1/0oa1pjdiymvj4VUrj0j7/aln1pjebh7EiN6cK20j7',
    },
    {
      text: 'Squares (QA)',
      url: 'https://login-stg.va.gov/home/va-stg_squaresqa_1/0oa1jwvqcjxO3SW2J0j7/aln1jx1drz747YcEz0j7',
    },
    {
      text: 'Squares (Dev)',
      url: 'https://login-stg.va.gov/home/va-stg_squaresssoedev_1/0oa1lrupkbH1ozeP80j7/aln1lrypwwJXLfiUM0j7',
    },
    {
      text: 'Squares (INT)',
      url: 'https://login-stg.va.gov/home/va-stg_squaresssoe2622int_1/0oa1my6mh8kphwFUh0j7/aln1my7eqaqhIJh300j7',
    },
    {
      text: 'Squares (PERF)',
      url: 'https://login-stg.va.gov/home/va-stg_squaresssoe2622perf_1/0oa1nora612vxL7IX0j7/aln1nou2yzrB1XSrB0j7',
    },
    {
      text: 'VA Caregiver Support (Dev)',
      url: 'https://va--ssoedev.sandbox.my.site.com/CARMAVendor',
    },
    {
      text: 'VA Caregiver Support (INT)',
      url: 'https://www.my.int.va.gov/CARMAVendor',
    },
    {
      text: 'VA Caregiver Support (Staging)',
      url: 'https://www.my.staging.va.gov/CARMAVendor',
    },
    {
      text: 'VAeDelivery (SQA)',
      url: 'https://test.vaedelivery.gdit.com',
    },
    {
      text: 'VAeDelivery (PreProd)',
      url: 'https://preprod.vaedelivery.ucap.gdit.com',
    },
    {
      text: 'VA Life Insurance (VALife) (UAT)',
      url: 'https://insuranceuat.vba.va.gov/home',
    },
    {
      text: 'VA Loan Electronic Reporting Interface Reengineering (VALERI-R) (Dev)',
      url: 'https://va--ssoedev.sandbox.my.site.com/VALERI',
    },
    {
      text: 'VA Loan Electronic Reporting Interface Reengineering (VALERI-R) (Int)',
      url: 'https://www.my.int.va.gov/VALERI',
    },
    {
      text: 'VA Loan Electronic Reporting Interface Reengineering (VALERI-R) (Staging)',
      url: 'https://www.my.staging.va.gov/VALERI',
    },
    {
      text: 'Veteran travel claim entry (BTSSS) (Int)',
      url: 'https://dev2-benetravelclaims-va.powerappsportals.us/',
    },
    {
      text: 'Veteran travel claim entry (BTSSS) (SQA)',
      url: 'https://dvagov-btsss-qa.dynamics365portals.us',
    },
    {
      text: 'Veteran travel claim entry (BTSSS) (PreProd)',
      url: 'https://dvagov-btsss-qa.dynamics365portals.us',
    },
    {
      text: 'Veterans Exposure Team - Health Outcomes Military Exposures (VET-HOME) (Int)',
      url: 'https://vethome-dev.powerappsportals.us/',
    },
    {
      text: 'Veterans Exposure Team - Health Outcomes Military Exposures (VET-HOME) (SQA)',
      url: 'https://vethome-qa.powerappsportals.us/',
    },
    {
      text: 'Veterans Exposure Team - Health Outcomes Military Exposures (VET-HOME) (PreProd)',
      url: 'https://vethome-preprod.powerappsportals.us/',
    },
    {
      text: 'Veterans Health Venture Studio (Int)',
      url: 'https://dvagov-vhventures-dev-portal.powerappsportals.us',
    },
    {
      text: 'Veterans Health Venture Studio (SQA)',
      url: 'https://dvagov-vhventures-test-portal.powerappsportals.us',
    },
    {
      text: 'Veterans Health Venture Studio (PreProd)',
      url: 'https://dvagov-vhventures-test-portal.powerappsportals.us',
    },
    {
      text: 'Veterans ID Card (VIC) (Int)',
      url: 'https://int.eauth.va.gov/accessva/?cspSelectFor=vic',
    },
    {
      text: 'Veterans ID Card (VIC) (SQA)',
      url: 'https://sqa.eauth.va.gov/accessva/?cspSelectFor=vic',
    },
    {
      text: 'Veterans ID Card (VIC) (PreProd)',
      url: 'https://preprod.eauth.va.gov/accessva/?cspSelectFor=vic',
    },
    {
      text: 'VTA IDES (Int)',
      url: 'https://login-stg.va.gov/home/va-stg_vtassoe2945int_1/0oa1xpkieaWRooxcL0j7/aln1xpoqmkmq6URqo0j7',
    },
    {
      text: 'VTA IDES (DEV)',
      url: 'https://login-stg.va.gov/home/va-stg_vtassoe2945dev_1/0oa1p5fd38T0wTiTo0j7/aln1p5ka24kBaSHFA0j7',
    },
    {
      text: 'VTA IDES (PreProd)',
      url: 'https://login-stg.va.gov/home/va-stg_vtassoe2945preprod_1/0oa1yqur10Qvz7YXo0j7/aln1yqvfq9fkbJ9LV0j7',
    },
    {
      text: 'VetBiz Portal (Dev)',
      url: 'https://login-stg.va.gov/home/va-stg_test_1/0oa1m0xi5lo1jr0ZR0j7/aln1m0yppa2JUjx7b0j7',
    },
    {
      text: 'VetBiz Portal (Int)',
      url: 'https://int.eauth.va.gov/accessva/?cspSelectFor=vetbiz',
    },
    {
      text: 'VetBiz Portal (SQA)',
      url: 'https://sqa.eauth.va.gov/accessva/?cspSelectFor=vetbiz',
    },
    {
      text: 'VetBiz Portal (PreProd)',
      url: 'https://preprod.eauth.va.gov/accessva/?cspSelectFor=vetbiz',
    },
    {
      text: 'VetRide (Staging)',
      url: 'https://staging.va.gov/sign-in/?app_name=VetRide+%5B2069%5D+Staging&client_id=okta_stg&code_challenge=bxEz1CS0OkpTl6pRB8h5XiQp9JUclaJeV6cK0O5QcfI&code_challenge_method=S256&oauth=true&state=Y1Z5TkJKQzg4ZFo5Ky9TNVFSbEhDTURkNUtneCt4V1hzKzNWNVc0NzhCeUxtaEJMNmo5cXdxLzlOSVJpdlNvWg',
    },
    {
      text: 'Veteran Health Identification Card (VHIC)',
      url: 'https://sqa.eauth.va.gov/accessva/?cspSelectFor=vhic-ss',
    },
    {
      text: 'VLogicFM (Int)',
      url: 'https://test.vlogicfm.us',
    },
    {
      text: 'VLogicFM (SQA)',
      url: 'https://test.vlogicfm.us',
    },
    {
      text: 'VLogicFM (PreProd)',
      url: 'https://training.vlogicfm.us',
    },
    {
      text: 'yourIT - Employee Self Service (Int)',
      url: 'https://int.eauth.va.gov/accessva/?cspSelectFor=yourit&targetPath=%2Fselfservice',
    },
    {
      text: 'yourIT - Employee Self Service (SQA)',
      url: 'https://sqa.eauth.va.gov/accessva/?cspSelectFor=yourit&targetPath=%2Fselfservice',
    },
    {
      text: 'yourIT - Employee Self Service (PreProd)',
      url: 'https://preprod.eauth.va.gov/accessva/?cspSelectFor=yourit&targetPath=%2Fselfservice',
    },
    {
      text: 'yourIT - Employee Self Service (Dev II)',
      url: 'https://login-stg.va.gov/home/va-stg_servicenowyourit2231nonproddevii_1/0oa1m21mfouQ8D7TV0j7/aln1m250iduR82Lb30j7',
    },
  ],
};

/**
 * Determines if the current environment is staging based on the domain
 * @returns {boolean} True if staging environment
 */
function isStaging() {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return hostname.includes('staging');
}

/**
 * Applies feature toggle overrides to links
 * @param {object} links - Links object with veterans and business arrays
 * @param {object} toggles - Feature toggle values from useFeatureToggle hook
 * @param {boolean} staging - Whether this is the staging environment
 * @returns {object} Links object with URLs potentially updated by feature toggles
 */
function applyFeatureToggles(links, toggles = {}, staging = false) {
  if (!toggles || Object.keys(toggles).length === 0) {
    return links;
  }

  // Select the appropriate Okta URL overrides based on environment
  const OKTA_URL_OVERRIDES = staging
    ? STAGING_OKTA_URL_OVERRIDES
    : PRODUCTION_OKTA_URL_OVERRIDES;

  const applyToggleToLink = link => {
    const text = link.text.toLowerCase();

    // Map link text to toggle key and Okta URL override
    // Same toggle names work in both environments, but URLs differ
    if (
      text.includes('box high impact') &&
      toggles.accessVaBoxHighImpactOktaurl &&
      OKTA_URL_OVERRIDES.boxHighImpact
    ) {
      return { ...link, url: OKTA_URL_OVERRIDES.boxHighImpact };
    }
    if (
      text.includes('box') &&
      !text.includes('high impact') &&
      toggles.accessVaBoxOktaurl &&
      OKTA_URL_OVERRIDES.box
    ) {
      return { ...link, url: OKTA_URL_OVERRIDES.box };
    }
    if (
      text.includes('community care') &&
      toggles.accessVaCcraOktaurl &&
      OKTA_URL_OVERRIDES.ccra
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.ccra)) {
        const match = OKTA_URL_OVERRIDES.ccra.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.ccra };
    }
    if (
      text.includes('centralized mail') &&
      toggles.accessVaCentralizedMailOktaurl &&
      OKTA_URL_OVERRIDES.centralizedMail
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.centralizedMail)) {
        const match = OKTA_URL_OVERRIDES.centralizedMail.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.centralizedMail };
    }
    if (
      text.includes('edelivery') &&
      toggles.accessVaEdeliveryOktaurl &&
      OKTA_URL_OVERRIDES.edelivery
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.edelivery)) {
        const match = OKTA_URL_OVERRIDES.edelivery.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.edelivery };
    }
    if (
      text.includes('education development') &&
      toggles.accessVaEdmsOktaurl &&
      OKTA_URL_OVERRIDES.edms
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.edms)) {
        const match = OKTA_URL_OVERRIDES.edms.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.edms };
    }
    if (
      text.includes('fiduciary') &&
      toggles.accessVaFastOktaurl &&
      OKTA_URL_OVERRIDES.fast
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.fast)) {
        const match = OKTA_URL_OVERRIDES.fast.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.fast };
    }
    if (
      text.includes('grants4vets') &&
      toggles.accessVaGrants4vetsOktaurl &&
      OKTA_URL_OVERRIDES.grants4vets
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.grants4vets)) {
        const match = OKTA_URL_OVERRIDES.grants4vets.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.grants4vets };
    }
    if (
      text.includes('homeless management') &&
      toggles.accessVaHmisOktaurl &&
      OKTA_URL_OVERRIDES.hmis
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.hmis)) {
        const match = OKTA_URL_OVERRIDES.hmis.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.hmis };
    }
    if (
      text.includes('iam invitation') &&
      toggles.accessVaIamInvitationOktaurl &&
      OKTA_URL_OVERRIDES.iamInvitation
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.iamInvitation)) {
        const match = OKTA_URL_OVERRIDES.iamInvitation.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.iamInvitation };
    }
    if (
      text.includes('kahua') &&
      toggles.accessVaKahuaOktaurl &&
      OKTA_URL_OVERRIDES.kahua
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.kahua)) {
        const match = OKTA_URL_OVERRIDES.kahua.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.kahua };
    }
    if (
      text.includes('life insurance online') &&
      toggles.accessVaLifeInsuranceOktaurl &&
      OKTA_URL_OVERRIDES.lifeInsurance
    ) {
      return { ...link, url: OKTA_URL_OVERRIDES.lifeInsurance };
    }
    if (
      text.includes('mceq vendor') &&
      toggles.accessVaMceqVendorPortalOktaurl &&
      OKTA_URL_OVERRIDES.mceqVendorPortal
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.mceqVendorPortal)) {
        const match = OKTA_URL_OVERRIDES.mceqVendorPortal.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.mceqVendorPortal };
    }
    if (
      text.includes('movelinq approval') &&
      toggles.accessVaMoveliqApprovalOktaurl &&
      OKTA_URL_OVERRIDES.moveliqApproval
    ) {
      // Skip override if already has environment label
      if (text.includes('(')) {
        return link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.moveliqApproval };
    }
    if (
      text.includes('movelinq employee') &&
      toggles.accessVaMoveliqEmployeeOktaurl &&
      OKTA_URL_OVERRIDES.moveliqEmployee
    ) {
      // Skip override if already has environment label
      if (text.includes('(')) {
        return link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.moveliqEmployee };
    }
    if (
      text.includes('movelinq expense') &&
      toggles.accessVaMoveliqExpenseOktaurl &&
      OKTA_URL_OVERRIDES.moveliqExpense
    ) {
      // Skip override if already has environment label
      if (text.includes('(')) {
        return link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.moveliqExpense };
    }
    if (
      text.includes('million veteran program') &&
      toggles.accessVaMvpOktaurl &&
      OKTA_URL_OVERRIDES.mvp
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.mvp)) {
        const match = OKTA_URL_OVERRIDES.mvp.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.mvp };
    }
    if (
      text.includes('puppies assisting') &&
      toggles.accessVaPawsOktaurl &&
      OKTA_URL_OVERRIDES.paws
    ) {
      return { ...link, url: OKTA_URL_OVERRIDES.paws };
    }
    if (
      text.includes('prosthetics vendor') &&
      toggles.accessVaProstheticsVendorOktaurl &&
      OKTA_URL_OVERRIDES.prostheticsVendor
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.prostheticsVendor)) {
        const match = OKTA_URL_OVERRIDES.prostheticsVendor.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.prostheticsVendor };
    }
    if (
      text.includes('qgenda') &&
      toggles.accessVaQgendaOktaurl &&
      OKTA_URL_OVERRIDES.qgenda
    ) {
      return { ...link, url: OKTA_URL_OVERRIDES.qgenda };
    }
    if (
      text.includes('quicksubmit') &&
      toggles.accessVaQuicksubmitOktaurl &&
      OKTA_URL_OVERRIDES.quicksubmit
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.quicksubmit)) {
        const match = OKTA_URL_OVERRIDES.quicksubmit.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      // The business (includes PIV) link keeps its own URL
      if (text.includes('includes piv')) {
        return link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.quicksubmit };
    }
    if (
      text.includes('remote order entry') &&
      toggles.accessVaRoesOktaurl &&
      OKTA_URL_OVERRIDES.roes
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.roes)) {
        const match = OKTA_URL_OVERRIDES.roes.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.roes };
    }
    if (
      text.includes('sourcetrack') &&
      toggles.accessVaSourcetrackOktaurl &&
      OKTA_URL_OVERRIDES.sourcetrack
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.sourcetrack)) {
        const match = OKTA_URL_OVERRIDES.sourcetrack.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.sourcetrack };
    }
    if (
      text.includes('sports4vets') &&
      text.includes('officials') &&
      toggles.accessVaSports4vetsOfficialsOktaurl &&
      OKTA_URL_OVERRIDES.sports4vetsOfficials
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.sports4vetsOfficials)) {
        const match = OKTA_URL_OVERRIDES.sports4vetsOfficials.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.sports4vetsOfficials };
    }
    if (
      text.includes('sports4vets') &&
      !text.includes('officials') &&
      toggles.accessVaSports4vetsOktaurl &&
      OKTA_URL_OVERRIDES.sports4vets
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.sports4vets)) {
        const match = OKTA_URL_OVERRIDES.sports4vets.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.sports4vets };
    }
    if (
      text.includes('squares') &&
      toggles.accessVaSquaresOktaurl &&
      OKTA_URL_OVERRIDES.squares
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.squares)) {
        const match = OKTA_URL_OVERRIDES.squares.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.squares };
    }
    if (
      text.includes('va caregiver support') &&
      toggles.accessVaVaCaregiverSupportOktaurl &&
      OKTA_URL_OVERRIDES.vaCaregiverSupport
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.vaCaregiverSupport)) {
        const match = OKTA_URL_OVERRIDES.vaCaregiverSupport.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.vaCaregiverSupport };
    }
    if (
      text.includes('valeri-r') &&
      toggles.accessVaValerirOktaurl &&
      OKTA_URL_OVERRIDES.valerir
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.valerir)) {
        const match = OKTA_URL_OVERRIDES.valerir.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.valerir };
    }
    if (
      text.includes('va life insurance') &&
      toggles.accessVaValifeOktaurl &&
      OKTA_URL_OVERRIDES.valife
    ) {
      return { ...link, url: OKTA_URL_OVERRIDES.valife };
    }
    if (
      text.includes('vetbiz') &&
      toggles.accessVaVetbizOktaurl &&
      OKTA_URL_OVERRIDES.vetbiz
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.vetbiz)) {
        const match = OKTA_URL_OVERRIDES.vetbiz.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.vetbiz };
    }
    if (
      text.includes('vet-home') &&
      toggles.accessVaVetHomeOktaurl &&
      OKTA_URL_OVERRIDES.vetHome
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.vetHome)) {
        const match = OKTA_URL_OVERRIDES.vetHome.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.vetHome };
    }
    if (
      text.includes('veteran travel claim') &&
      toggles.accessVaVeteranTravelClaimOktaurl &&
      OKTA_URL_OVERRIDES.veteranTravelClaim
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.veteranTravelClaim)) {
        const match = OKTA_URL_OVERRIDES.veteranTravelClaim.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.veteranTravelClaim };
    }
    if (
      text.includes('veterans health venture') &&
      toggles.accessVaVeteransHealthVentureStudioOktaurl &&
      OKTA_URL_OVERRIDES.veteransHealthVentureStudio
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.veteransHealthVentureStudio)) {
        const match = OKTA_URL_OVERRIDES.veteransHealthVentureStudio.find(
          override => text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.veteransHealthVentureStudio };
    }
    if (
      text.includes('vetride') &&
      toggles.accessVaVetrideOktaurl &&
      OKTA_URL_OVERRIDES.vetride
    ) {
      return { ...link, url: OKTA_URL_OVERRIDES.vetride };
    }
    if (
      text.includes('personnel security investigations') &&
      toggles.accessVaVetrideOktaurl &&
      OKTA_URL_OVERRIDES.psip
    ) {
      return { ...link, url: OKTA_URL_OVERRIDES.psip };
    }
    if (
      text.includes('veteran health identification') &&
      toggles.accessVaVhicOktaurl &&
      OKTA_URL_OVERRIDES.vhic
    ) {
      return { ...link, url: OKTA_URL_OVERRIDES.vhic };
    }
    if (
      text.includes('veterans id card') &&
      !text.includes('health') &&
      toggles.accessVaVicOktaurl &&
      OKTA_URL_OVERRIDES.vic
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.vic)) {
        const match = OKTA_URL_OVERRIDES.vic.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.vic };
    }
    if (
      text.includes('vlogicfm') &&
      toggles.accessVaVlogicfmOktaurl &&
      OKTA_URL_OVERRIDES.vlogicfm
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.vlogicfm)) {
        const match = OKTA_URL_OVERRIDES.vlogicfm.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.vlogicfm };
    }
    if (
      text.includes('vta ides') &&
      toggles.accessVaVtaIdesOktaurl &&
      OKTA_URL_OVERRIDES.vtaIdes
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.vtaIdes)) {
        const match = OKTA_URL_OVERRIDES.vtaIdes.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.vtaIdes };
    }
    if (
      text.includes('yourit') &&
      toggles.accessVaYouritOktaurl &&
      OKTA_URL_OVERRIDES.yourit
    ) {
      // Match by environment label when override is an array
      if (Array.isArray(OKTA_URL_OVERRIDES.yourit)) {
        const match = OKTA_URL_OVERRIDES.yourit.find(override =>
          text.includes(override.label.toLowerCase()),
        );
        return match ? { ...link, url: match.url } : link;
      }
      return { ...link, url: OKTA_URL_OVERRIDES.yourit };
    }

    return link;
  };

  return {
    veterans: links.veterans.map(applyToggleToLink),
    business: links.business.map(applyToggleToLink),
  };
}

/**
 * Gets the appropriate links based on the current environment and feature toggles
 * @param {object} featureToggles - Optional feature toggle values to override URLs
 * @returns {object} Links object with veterans and business arrays
 */
export function getAccessVALinks(featureToggles) {
  const staging = isStaging();
  const baseLinks = staging ? STAGING_LINKS : PRODUCTION_LINKS;
  return applyFeatureToggles(baseLinks, featureToggles, staging);
}

// Export default as production links for backwards compatibility
export const ACCESSVA_LINKS = PRODUCTION_LINKS;
